import { Component, Output, EventEmitter, signal, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PreventLeadingSpaceDirective } from '@app/shared/components/utils/prevent-leading-space.directive';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ApiService } from '@app/core/services/API/api-service';
import { ToastrService } from 'ngx-toastr';
import { Spinner } from '@app/shared/components/spinner/spinner'; // Custom spinner component



export class CustomDateAdapter extends DateAdapter<Date> {
  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  
  // Implement other required methods as pass-throughs
  override parse(value: any): Date | null {
    return value ? new Date(value) : null;
  }
  override getDate(date: Date): number {
    return date.getDate();
  }
  override getMonth(date: Date): number {
    return date.getMonth();
  }
  override getYear(date: Date): number {
    return date.getFullYear();
  }
  override getDayOfWeek(date: Date): number {
    return date.getDay();
  }
  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2000, i, 1);
      months.push(date.toLocaleDateString('en-US', { month: style }));
    }
    return months;
  }
  override getDateNames(): string[] {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push(i.toString());
    }
    return days;
  }
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(2000, 0, 2 + i); // Start from Sunday
      days.push(date.toLocaleDateString('en-US', { weekday: style }));
    }
    return days;
  }
  override getYearName(date: Date): string {
    return date.getFullYear().toString();
  }
  override getFirstDayOfWeek(): number {
    return 0; // Sunday
  }
  override getNumDaysInMonth(date: Date): number {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  }
  override addCalendarYears(date: Date, years: number): Date {
    return new Date(date.getFullYear() + years, date.getMonth(), date.getDate());
  }
  override addCalendarMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
  }
  override addCalendarDays(date: Date, days: number): Date {
    return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
  }
  override toIso8601(date: Date): string {
    return date.toISOString();
  }
  override isDateInstance(obj: any): boolean {
    return obj instanceof Date;
  }
  override isValid(date: Date): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }
  override invalid(): Date {
    return new Date(NaN);
  }
  override clone(date: Date): Date {
    return new Date(date.getTime());
  }
  override createDate(year: number, month: number, date: number): Date {
    return new Date(year, month, date);
  }
  override today(): Date {
    return new Date();
  }
}

// Custom date format configuration
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MMMM D, YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'MMMM D, YYYY',
    monthYearA11yLabel: 'MMM Y',
  },
};
export interface Person {
  name: string;
  email: string;
  phone: string;
  dateOfBirth: Date | null;
  jobInfo: string;
  company: string;
  jobRoleID: string;
  timezone: string;
  homeTeamID: string;
  managerID: string;
  accesslevel: string;
  utype: string;
  address: string;
  isActive: boolean;
  description?: string;
}


@Component({
  selector: 'app-add-people-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectModule,
    MatProgressSpinnerModule,
    PreventLeadingSpaceDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    Spinner
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-US' }
  ],
  templateUrl: './add-people-form.html',
  styleUrl: './add-people-form.scss'
})
export class AddPeopleForm implements OnInit {
  @Output() personSubmitted = new EventEmitter<Person>();
  @Output() close = new EventEmitter<void>();

  // Loading state
  isLoading = false;

  // Form Controls for validation
  personNameControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  emailControl = new FormControl('', [Validators.required, Validators.email]);
  phoneControl = new FormControl('', [Validators.pattern(/^[\+]?[1-9][\d]{0,15}$/),Validators.required]);
  dobControl = new FormControl(null, [Validators.required, this.validateAge.bind(this)]);
  companyControl = new FormControl(null);
  jobTitleControl = new FormControl('', [Validators.required, Validators.minLength(2)]);
  jobRoleControl = new FormControl(null);
  timezoneControl = new FormControl(null, [Validators.required]);
  homeTeamControl = new FormControl(null, [Validators.required]);
  managerControl = new FormControl(null);
  accessLevelControl = new FormControl(null, [Validators.required]);
  addressControl = new FormControl('');
  isActiveControl = new FormControl(true);
  descriptionControl = new FormControl('');

  // Data signals
  jobRoles = signal<any[]>([]);
  homeTeams = signal<any[]>([]);
  managers = signal<any[]>([]);
  companies = signal<any[]>([]);

  // Static options
  timezoneOptions: any[] = [];

  accessLevelOptions :any[] = [ ];

  // Services
  private apiService = inject(ApiService);
  private toastr = inject(ToastrService);

  ngOnInit(): void {
    this.loadJobRoles();
    this.loadTeams();
    this.loadManagers();
    this.loadAccessLevels();
    this.loadTimezones();
    this.loadCompanies();
  }

  onClose(): void {
    this.close.emit();
  }
  toggleActiveStatus(): void {
    this.isActiveControl.setValue(!this.isActiveControl.value);
  }
  isFormValid(): boolean {
    return this.personNameControl.valid && 
           this.emailControl.valid && 
           this.phoneControl.valid && 
           this.dobControl.valid && 
           this.jobTitleControl.valid && 
           this.timezoneControl.valid && 
           this.homeTeamControl.valid && 
           this.accessLevelControl.valid;
  }

  // Custom validator for age (must be at least 16 years old)
  private validateAge(control: FormControl): {[key: string]: any} | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }
    
    const today = new Date();
    const birthDate = new Date(control.value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    if (age < 16) {
      return { 'minAge': { value: control.value, minAge: 16 } };
    }
    
    if (age > 100) {
      return { 'maxAge': { value: control.value, maxAge: 100 } };
    }
    
    return null;
  }

  private loadJobRoles(): void {
    this.apiService.getJobRole().subscribe({
      next: (res: any) => {
        const jobRoles = res.data || res;
        this.jobRoles.set(Array.isArray(jobRoles) ? jobRoles : []);
      },
      error: (error) => {
        console.error('Error loading job roles:', error);
        this.jobRoles.set([]);
      }
    });
  }

  private loadTeams(): void {
    this.apiService.getHomeTeamList().subscribe({
      next: (res: any) => {
        const teams = res.data || res;
        this.homeTeams.set(Array.isArray(teams) ? teams : []);
      },
      error: (error) => {
        console.error('Error loading teams:', error);
        this.homeTeams.set([]);
      }
    });
  }

  private loadAccessLevels(): void {
    this.apiService.getAccessLevelList().subscribe({
      next: (res: any) => {
        const accessLevels = res.data || res;
        this.accessLevelOptions = Array.isArray(accessLevels) ? accessLevels : [];
      },
      error: (error) => {
        console.error('Error loading access levels:', error);
        this.accessLevelOptions = [];
      }
    });

  }
  
  private loadTimezones(): void {
    this.apiService.getTimeZoneList().subscribe({
      next: (res: any) => {
        const timezones = res.data || res;
        this.timezoneOptions = Array.isArray(timezones) ? timezones : [];
      }
      ,
      error: (error) => { 
        console.error('Error loading timezones:', error);
        this.timezoneOptions = [];
      }
    });
  }

  private loadManagers(): void {
    this.apiService.getManagerData().subscribe({
      next: (res: any) => {
        const users = res.data || res;
        // Filter users who can be managers (you might want to add a role check here)
        this.managers.set(Array.isArray(users) ? users : []);
      },
      error: (error) => {
        console.error('Error loading managers:', error);
        this.managers.set([]);
      }
    });
  }
  
  private loadCompanies(): void {
    this.apiService.getCompanyList().subscribe({
      next: (res: any) => {
        const companies = res.data || res;
        this.companies.set(Array.isArray(companies) ? companies : []);
      },
      error: (error) => {
        console.error('Error loading companies:', error);
        this.companies.set([]);
      }
    });
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid() || this.isLoading) return;

    this.isLoading = true;

    try {
      const personData: Person = {
        name: this.personNameControl.value || '',
        email: this.emailControl.value || '',
        phone: this.phoneControl.value || '',
        dateOfBirth: this.dobControl.value,
        jobInfo: this.jobTitleControl.value || '',
        company: this.companyControl.value || '',
        jobRoleID: this.jobRoleControl.value || '',
        timezone: this.timezoneControl.value || '',
        homeTeamID: this.homeTeamControl.value || '',
        managerID: this.managerControl.value || '',
        accesslevel: this.accessLevelControl.value || '',
        address: this.addressControl.value || '',
        isActive: this.isActiveControl.value || false,
        utype: 'user',
        description: this.descriptionControl.value || ''
      };

      // Call API to create person
      const result = await this.apiService.CreatePeople(personData).toPromise();
      // Update personData with the response data if available
      const finalPersonData = {
        ...personData,
        _id: result.data?._id || '',
        createdAt: result.data?.createdAt || new Date().toISOString(),
        updatedAt: result.data?.updatedAt || new Date().toISOString()
      };
      
      this.personSubmitted.emit(finalPersonData);
      this.toastr.success(result.message || 'Person created successfully!');
      this.onClose();
    } catch (error: any) {
      console.error('Error creating person:', error);
      this.toastr.error(error.message || 'Failed to create person. Please try again.');
    } finally {
      this.isLoading = false;
    }
  }

 

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
